import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { useLocation } from "react-router-dom";
import "./Wellness.css"; // optional (create if you want)

function Wellness() {
  const location = useLocation();

  const WELLNESS_OPTIONS = useMemo(
    () => [
      "Complementary Doctor On Call",
      "Preemployment Health Checkup",
      "Comprehensive Health Checkup",
      "Complementary Health Camp",
      "Yoga & More",
      "GYM & SPA Membership",
      "Naturopathy",
      "Book Virtual Session",
    ],
    []
  );

  const [formData, setFormData] = useState({
    wellnessOption: "",
    companyName: "",
    employeeCode: "",
    employeeId: "",
    contactNo: "",
    emailId: "",
    relationship: "",
  });

  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // ✅ (Optional) Restore cached data
  useEffect(() => {
    const cached = JSON.parse(sessionStorage.getItem("wellnessData") || "null");
    if (cached) setFormData(cached);
  }, []);

  // ✅ (Optional) Cache while typing
  useEffect(() => {
    sessionStorage.setItem("wellnessData", JSON.stringify(formData));
  }, [formData]);

  // ✅ Step 1: Fetch Company Name using accountId (same as ClaimIntimation)
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const accountId = query.get("accountId") || sessionStorage.getItem("selectedAccount");
    if (!accountId) return;

    const fetchAccountData = async () => {
      try {
        const res = await axios.get(`https://api.cri.co.in/api/accounts/${accountId}`);

        sessionStorage.setItem("selectedAccount", accountId);
        if (res.data?.hrNumber) sessionStorage.setItem("mobileNumber", res.data.hrNumber);

        if (res.data?.accountName) {
          setFormData((prev) => ({
            ...prev,
            companyName: res.data.accountName,
          }));
        }
      } catch (err) {
        console.error("❌ Error fetching account data:", err);
      }
    };

    fetchAccountData();
  }, [location.search]);

  // ✅ Step 2: Auto-fetch employee details (Self/Employee row) same like ClaimIntimation
  useEffect(() => {
    const accountId = sessionStorage.getItem("selectedAccount");
    const role = (sessionStorage.getItem("role") || "").toLowerCase();

    const mobileNumber =
      role === "hr"
        ? sessionStorage.getItem("mobileNumber") // HR → use HR mobile number
        : sessionStorage.getItem("employeeNumber"); // Employee → stored here (as in your app)

    if (!accountId || !mobileNumber) {
      console.warn("❌ Missing accountId or mobileNumber in sessionStorage");
      return;
    }

    const fetchSelfDetails = async () => {
      try {
        const res = await fetch(
          `https://api.cri.co.in/api/account/${accountId}/live-data-by-mobile/${mobileNumber}`
        );
        const data = await res.json();

        if (!res.ok) {
          console.error("Error fetching live data:", data?.error || data);
          return;
        }

        const selfRow = (data || []).find(
          (item) =>
            item?.benef_relation?.toLowerCase() === "self" ||
            item?.benef_relation?.toLowerCase() === "employee" ||
            item?.benef_relation?.toLowerCase() === "emp"
        );

        if (!selfRow) {
          console.warn("⚠️ No 'Self' record found for this mobile number");
          return;
        }

        // ✅ Prefill auto fields
        setFormData((prev) => ({
          ...prev,
          employeeCode: selfRow.pribenef_employee_code || "",
          employeeId: selfRow["Card No"] || selfRow.employee_id || "", // fallback if your API has another key
          emailId: selfRow.benef_email || "",
          // contactNo + relationship should remain manual as you requested
        }));
      } catch (error) {
        console.error("❌ Error fetching Self details:", error);
      }
    };

    fetchSelfDetails();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // ✅ Example payload (adjust endpoint as per your backend)
      const payload = {
        wellnessOption: formData.wellnessOption,
        companyName: formData.companyName,
        employeeCode: formData.employeeCode,
        employeeId: formData.employeeId,
        contactNo: formData.contactNo,
        emailId: formData.emailId,
        relationship: formData.relationship,
      };

      // 🔻 Replace with your real API:
      const res = await fetch("https://api.cri.co.in/api/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        alert("Server error: " + txt);
        return;
      }

      setOpenSnackbar(true);

      // ✅ clear only manual fields + option (auto fields stay fetched)
      setFormData((prev) => ({
        ...prev,
        wellnessOption: "",
        contactNo: "",
        relationship: "",
      }));

      // optional: clear cache after submit
      sessionStorage.removeItem("wellnessData");
    } catch (err) {
      console.error(err);
      alert("Failed to submit. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          boxShadow: 4,
          p: 4,
          mt: 4,
          borderRadius: 3,
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
          transition: "0.3s",
          "&:hover": { boxShadow: 6 },
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          align="center"
          sx={{ fontWeight: "bold", color: "#0d47a1" }}
        >
          Wellness Request
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* ✅ Wellness option dropdown */}
            <Grid item xs={12}>
              <TextField
                select
                label="Wellness Option"
                name="wellnessOption"
                value={formData.wellnessOption}
                onChange={handleChange}
                fullWidth
                required
                SelectProps={{ native: true }}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  border: "1px solid #1976d2",
                }}
              >
                <option value=""></option>
                {WELLNESS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </TextField>
            </Grid>

            {/* ✅ Auto fetched (readonly) */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                fullWidth
                required
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                  border: "1px solid #1976d2",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Employee Code"
                name="employeeCode"
                value={formData.employeeCode}
                fullWidth
                required
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                  border: "1px solid #1976d2",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Employee ID"
                name="employeeId"
                value={formData.employeeId}
                fullWidth
                required
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                  border: "1px solid #1976d2",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email ID"
                name="emailId"
                value={formData.emailId}
                fullWidth
                required
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                  border: "1px solid #1976d2",
                }}
              />
            </Grid>

            {/* ✅ Manual fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Number"
                name="contactNo"
                value={formData.contactNo}
                onChange={handleChange}
                fullWidth
                required
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  border: "1px solid #1976d2",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Relationship"
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                fullWidth
                required
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  border: "1px solid #1976d2",
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  backgroundColor: "#0d47a1",
                  "&:hover": { backgroundColor: "#0b3c8c" },
                  padding: "12px",
                  borderRadius: 2,
                  fontWeight: "bold",
                  boxShadow: "0 3px 5px rgba(0, 0, 0, 0.3)",
                  position: "relative",
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={24}
                      sx={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        marginLeft: "-12px",
                        marginTop: "-12px",
                      }}
                    />
                    <span style={{ visibility: "hidden" }}>Submitting...</span>
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: "100%" }}>
          Submitted successfully!
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Wellness;